import { useQuery } from '@tanstack/react-query';
import type { SynchronousQueryConfig } from 'lib/react-query';
import type { CreativeParams } from '../../creatives/api';
import { getCreatives } from '../../creatives/api';
import queryKeys from './queryKeys';
import type { HypothesisCreativeEntry, HypothesisSection, UploadedFilter, } from '../types/hypothesis';
import type { CreativeEntry } from 'types/shared';

export interface TestCreativesParams {
  app?: string;
  hypothesis?: string[];
  customer?: string;
  flow?: string[];
  technology?: string[];
  status?: string;
  query?: string;
  tpc_finish_date?: string;
  uploaded?: UploadedFilter;
}

type TestCreativesApiEntry = Omit<HypothesisCreativeEntry, 'section'> & {
  test_period?: HypothesisSection | null;
  section?: HypothesisSection | null;
};

const toHypothesisCreativeEntry = ({
  test_period,
  section,
  ...rest
}: TestCreativesApiEntry): HypothesisCreativeEntry => ({
  ...rest,
  section: section ?? test_period ?? null,
});

const toFallbackHypothesisCreativeEntry = (
  creative: CreativeEntry,
): HypothesisCreativeEntry => ({
  ...creative,
  hypothesis: null,
  section: null,
  launch_status: 'not_launched',
  flow: null,
  customer: null,
  tpc_finish_date: null,
  tpc_model: null,
  concept: null,
  variable: null,
  idea_type: null,
  headline: null,
  technology: null,
  preview_link: null,
  uploaded: creative.created,
});

export const getTestCreatives = async (
  { params }: { params: TestCreativesParams },
  signal?: AbortSignal,
): Promise<HypothesisCreativeEntry[]> => {
  const { app, uploaded, ...filters } = params;
  const filterParams = Object.fromEntries(
    Object.entries(filters).filter(([, v]) =>
      Array.isArray(v) ? v.length > 0 : !!v,
    ),
  );
  const uploadDateFilter =
    uploaded && uploaded !== 'all' ? { upload_date: uploaded } : {};

  const creativeParamsWithHypothesis: CreativeParams = {
    app,
    with_hypothesis: true,
    ...filterParams,
    ...uploadDateFilter,
  };
  const creativeParamsWithoutHypothesis: CreativeParams = {
    app,
    ...filterParams,
    ...uploadDateFilter,
  };

  const [hypothesisData, fallbackData] = await Promise.all([
    getCreatives({ params: creativeParamsWithHypothesis }, signal),
    getCreatives({ params: creativeParamsWithoutHypothesis }, signal),
  ]);

  const byId = new Map<string, HypothesisCreativeEntry>();

  for (const creative of hypothesisData as TestCreativesApiEntry[]) {
    byId.set(creative.id, toHypothesisCreativeEntry(creative));
  }

  for (const creative of fallbackData) {
    if (byId.has(creative.id)) continue;
    byId.set(creative.id, toFallbackHypothesisCreativeEntry(creative));
  }

  return [...byId.values()];
};

interface UseTestCreativesOptions {
  params: TestCreativesParams;
  config?: SynchronousQueryConfig<typeof getTestCreatives, HypothesisCreativeEntry[]>;
}

export const useTestCreatives = ({ params, config }: UseTestCreativesOptions) => {
  return useQuery({
    staleTime: 0,
    refetchOnMount: true,
    ...config,
    queryFn: ({ signal }) => getTestCreatives({ params }, signal),
    queryKey: queryKeys.testCreatives<TestCreativesParams>(params),
  });
};
